/** global: localStorage */
import {
	ExpiringRegistryProxy,
	PrefixedRegistryProxy,
	SizeLimitedRegistryProxy,
	StorageDriver,
	onDocumentReady
} from "silver";
import { PermissionService } from "core/Services/PermissionService";
import { BookModalService } from "core/Services/BookModalService";
import { BookDataService } from "core/Services/BookDataService";
import {
	Banner3dLayer,
	Banner3dLoader,
	MarginLayerPositionDriver,
	TransformLayerPositionDriver
} from "core/Controls/Banner3d";
import { DownloadBookService } from "core/Services/DownloadBookService";
import { ReadBookService } from "core/Services/ReadBookService";
import { ListURLEncoderService } from "core/Services/ListURLEncoderService";
import { BookBandService } from "core/Services/BookBandService";
import { LoginBuildingSearchService } from "core/Services/LoginBuildingSearchService";
import {
	type BookControlService,
	BookControlViewModeService,
	StudentBookControlService, TeacherBookControlService
} from "core/Services/BookControlService";
import { ExpanderService } from "core/Services/ExpanderService";
import { BookARQuizService } from "core/Services/BookARQuizService";
import { WindowLayout } from "core/WindowLayout";
import { BookControlMetricRepository } from "core/Data/BookControlMetricRepository";
import { AvatarIDService } from "core/Services/AvatarIDService";
import { SetUserGradeLevelService } from "core/Services/SetUserGradeLevelService";
import { PointOutService } from "core/Services/PointOutService";
import { BookReviewService } from "core/Services/BookReviewService";
import { BookProjectService } from "core/Services/BookProjectService";
import { SearchBooksService } from "core/Services/SearchBooksService";
import { BookMinimumGradeAccessService } from "core/Services/BookMinimumGradeAccessService";
import { ImageBandService } from "core/Services/ImageBandService";
import { AppPinService } from "core/Services/AppPinService";
import { Spinner } from "core/Controls/Spinner";
import { Sidesheet } from "core/Controls/SideSheet";
import { BookBundleMenu } from "core/Controls/Books/BookBundleMenu";
import { type MyonEnv, type UserAuthorizedMyonEnv, isEnvUserAuthorized } from "shared/Api/MyonEnv";
import { Role } from "shared/Api/Role";
import { Int32ArrayEncoder } from "shared/Int32ArrayEncoder";
import {
	$bookBundleService,
	$bookLaunchDataService,
	$client,
	$focusControl,
	$format,
	$getBookBagService,
	$getLanguageLoader,
	$httpClient,
	$markdown,
	$modal,
	$router,
	$transform,
	$userAgent,
	$userStorage
} from "shared/services";
import { cacheFirstResults } from "shared/functions";

export const $windowLayout = new WindowLayout();

export const $getSpinner = cacheFirstResults((): Spinner => {
	const spinner = new Spinner(4);
	onDocumentReady(() => document.body.appendChild(spinner.element));
	return spinner;
});

export const $expanderService = new ExpanderService($userStorage);
export const $avatarIDService = new AvatarIDService($httpClient, $router);
export const $pointOutService = new PointOutService($windowLayout, $focusControl);
export const $bookReviewService = new BookReviewService($router, $httpClient);
export const $bookProjectService = new BookProjectService($httpClient, $router);
export const $sidesheet = new Sidesheet($focusControl);
export const $searchBooksService = new SearchBooksService($router, $httpClient);
export const $bookDataService = new BookDataService($httpClient, $bookBundleService);
export const $readBookService = new ReadBookService($client, $router, $bookLaunchDataService);
export const $bookMinimumGradeAccessService = new BookMinimumGradeAccessService($httpClient);
export const $bookControlViewModeService = new BookControlViewModeService($userStorage);

export const $userListEncoderService = (() => {
	const cipher = new Int32ArrayEncoder();
	return new ListURLEncoderService($router, 100, new ExpiringRegistryProxy<number[]>(
		3600000, // one hour
		new SizeLimitedRegistryProxy(10,
			new PrefixedRegistryProxy("ule_",
				new StorageDriver(localStorage, {
					encode: (data): string => cipher.encode([ data.o, data.v.x, ...data.v.v ]),
					decode: (value: string) => {
						const [ order, expiration, ...values ] = cipher.decode(value);
						return { o: order, v: { x: expiration, v: values } };
					}
				})
			)
		)
	));
})();


export const $getLoginBuildingSearchService = cacheFirstResults((env: MyonEnv) => {
	let priorityHost : string|null = null;
	if (env.login.buildingSearch.prioritizeHost) {
		priorityHost = env.login.buildingSearch.canonicalHost || window.location.host;
	}

	return new LoginBuildingSearchService(
		$httpClient,
		env.login.buildingSearch.endpoint,
		priorityHost,
	);
});

export const $getBookARQuizService = cacheFirstResults((env: MyonEnv) => {
	return new BookARQuizService($client, $httpClient, $router, env, $modal, $getLanguageLoader(env));
});

export const $getDownloadBookService = cacheFirstResults((env: MyonEnv) => {
	return new DownloadBookService($userAgent, env.chromeAppId);
})

export const $getPermissionService = cacheFirstResults((env: MyonEnv) => new PermissionService(env));

export const $getAppPinService = cacheFirstResults((env: MyonEnv): AppPinService => {
	return new AppPinService(
		env.mobileApiDomain,
		$userAgent,
		$httpClient
	);
});


export const $getBookModalService = cacheFirstResults((env: MyonEnv) => {
	if (!isEnvUserAuthorized(env)) {
		throw new Error("User is not authorized");
	}
	const permissionService = $getPermissionService(env);
	return new BookModalService(
		$bookBundleService,
		$getBookBagService(env),
		$bookMinimumGradeAccessService,
		$bookDataService,
		$getBookARQuizService(env),
		$searchBooksService,
		$getDownloadBookService(env),
		$readBookService,
		$bookReviewService,
		permissionService,
		$modal,
		env.auth.userId,
		env.auth.roleId,
		env.canManageBookGradeLevelRestrictions,
		permissionService.hasMyList(),
		$getLanguageLoader(env)
	);
});

export const $getImageBandService = cacheFirstResults((env: MyonEnv) => new ImageBandService($transform, $getLanguageLoader(env)));

export const $getBookControlMetricRepository = cacheFirstResults(async (env: MyonEnv): Promise<BookControlMetricRepository> => {
	const langLoader = $getLanguageLoader(env);
	const [ books, user ] = await Promise.all([ langLoader.load("Books"), langLoader.load("User") ]);

	return new BookControlMetricRepository($format, user, books);
});

export const $getTeacherBookControlService = cacheFirstResults((env: MyonEnv): TeacherBookControlService => {
	const bookBundleMenu = new BookBundleMenu($bookBundleService, $getLanguageLoader(env));
	const service = new TeacherBookControlService(
		$bookDataService,
		$getBookModalService(env),
		$readBookService,
		$bookBundleService,
		bookBundleMenu,
		$getLanguageLoader(env),
		$getPermissionService(env),
	);
	service.onControlInitialized(() => document.body.classList.add("-hasBookControl"));

	$getBookControlMetricRepository(env).then(repo => {
		service.setCardMetrics([repo.arQuizLevelMetric]);
		service.setListMetrics([
			repo.arQuizLevelMetric,
			repo.gradeRangeMetric,
			repo.audioRuntimeMetric
		]);
	});

	return service;
});

export const $getStudentBookControlService = cacheFirstResults((env: UserAuthorizedMyonEnv) => {
	const permissionService = $getPermissionService(env);

	const service = new StudentBookControlService(
		env.auth.userId,
		permissionService.hasMyList(),
		$bookDataService,
		$getBookModalService(env),
		$getBookARQuizService(env),
		$readBookService,
		$bookBundleService,
		$getLanguageLoader(env),
		$windowLayout
	);

	service.onControlInitialized(() => document.body.classList.add("-hasBookControl"));

	$getBookControlMetricRepository(env).then(repo => {
		service.setListMetrics([
			repo.arQuizLevelMetric,
			repo.gradeRangeMetric,
			repo.audioRuntimeMetric
		]);
	});

	return service;
});

export const $getBookControlService = cacheFirstResults((env: MyonEnv): BookControlService => {
	if (!isEnvUserAuthorized(env)) {
		throw new Error("Not authorized");
	}
	if (env.auth.roleId === Role.STUDENT) {
		return $getStudentBookControlService(env);
	}
	return $getTeacherBookControlService(env);
});

export const $getSetUserGradeLevelService = cacheFirstResults((env: MyonEnv) => new SetUserGradeLevelService($httpClient, $router, env));

export const $getBookBandService = cacheFirstResults((env: MyonEnv) => {
	return new BookBandService($getBookControlService(env), $getLanguageLoader(env), $markdown, $transform, $httpClient);
});

export const $getBannerLoader = cacheFirstResults((env: MyonEnv): Banner3dLoader => {
	const Driver = $userAgent.browserIsIn(["Firefox"]) ?
		MarginLayerPositionDriver :
		TransformLayerPositionDriver;

	return new Banner3dLoader(
		$getBookModalService(env),
		$bookDataService,
		$httpClient,
		$userStorage,
		$getLanguageLoader(env),
		function buildManifestUrl(bannerURL) {
			return bannerURL + "banner.html";
		},
		function makeLayer(element) {
			return new Banner3dLayer(element, new Driver(element));
		}
	);
});
